import { OverlayPanel } from 'primereact/overlaypanel';
import { classNames } from 'primereact/utils';
import { MouseEventHandler, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import CertificatesFilledIcon from '../../../../assets/icons/CertificatesFilled';
import CertificatesOutlineIcon from '../../../../assets/icons/CertificatesOutline';
import ComponentsFilledIcon from '../../../../assets/icons/ComponentsFilled';
import ComponentsOutlineIcon from '../../../../assets/icons/ComponentsOutline';
import ProductsFilledIcon from '../../../../assets/icons/ProductsFilled';
import ProductsOutlineIcon from '../../../../assets/icons/ProductsOutline';
import RequestsFilledIcon from '../../../../assets/icons/RequestsFilled';
import RequestsOutlineIcon from '../../../../assets/icons/RequestsOutline';
import Logo, { LogoType } from '../../../../components/logo';
import { useAuth } from '../../../../hooks/auth/useAuth';
import { AppRoutes } from '../../../../shared/enums/app-routes';
import ContactSupport from './contact-support/ContactSupport';
import styles from './Header.module.css';
import HeaderLink from './header-link/HeaderLink';
import MobileMenu from './mobile-menu/MobileMenu';
import MobileMenuOverlay from './mobile-menu/MobileMenuOverlay';
import Notifications from './notifications/Notifications';
import UserButton from './user-button/UserButton';

const Header = () => {
  const { isAuthenticated, isBasfUser, user } = useAuth();

  const overlayPanelRef = useRef<OverlayPanel>(null);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  const onMenuClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    if (overlayPanelRef.current) {
      overlayPanelRef.current?.toggle(event);
    }
  };

  const onLocationChange = () => {
    overlayPanelRef.current?.hide();
  };

  const onOverlayPanelShow = () => {
    return setIsOverlayVisible(true);
  };

  const onOverlayPanelHide = () => {
    return setIsOverlayVisible(false);
  };

  useEffect(() => {
    onLocationChange();
  }, [location]);

  return (
    <>
      <div
        className={classNames(
          'flex justify-content-between align-items-center h-4rem sm:px-6',
          styles.header,
        )}
      >
        <Logo className={styles['logo']} type={LogoType.PACIFIC} white />

        {isAuthenticated && (
          <>
            <div className='h-full hidden lg:flex'>
              <HeaderLink
                href={AppRoutes.Products}
                label={t('global.productMenu')}
                icon={ProductsOutlineIcon}
                activeIcon={ProductsFilledIcon}
              />
              {!isBasfUser && (
                <HeaderLink
                  href={AppRoutes.Components}
                  label={t('global.componentsMenu')}
                  icon={ComponentsOutlineIcon}
                  activeIcon={ComponentsFilledIcon}
                />
              )}
              <HeaderLink
                href={AppRoutes.ProductTasksTab}
                label={t('global.tasksMenu')}
                icon={RequestsOutlineIcon}
                activeIcon={RequestsFilledIcon}
              />
              {user?.company?.isCertificateManagementEnabled && (
                <HeaderLink
                  href={AppRoutes.MyCertificatesTab}
                  label={t('global.certificatesMenu')}
                  icon={CertificatesOutlineIcon}
                  activeIcon={CertificatesFilledIcon}
                />
              )}
            </div>
            <div className='flex'>
              <Notifications />
              <MobileMenu isOpen={isOverlayVisible} onClick={onMenuClick} />
              <ContactSupport />
              <UserButton />
            </div>
          </>
        )}
      </div>
      <OverlayPanel
        ref={overlayPanelRef}
        className='w-full shadow-2 mt-2'
        pt={{
          content: {
            className: 'flex flex-column align-items-center',
          },
        }}
        onShow={onOverlayPanelShow}
        onHide={onOverlayPanelHide}
      >
        <MobileMenuOverlay />
      </OverlayPanel>
    </>
  );
};

export default Header;
